import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, ShieldAlert } from 'lucide-react';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import {
  type AchPositivePayRuleResponse,
  transferAchPositivePayRulesDeleteAchPositivePayRuleMutation,
  transferAchPositivePayRulesListAchPositivePayRulesByBankAccountOptions,
  transferAchPositivePayRulesListAchPositivePayRulesByBankAccountQueryKey,
} from '@column/api';
import { Icon, Button as RowActionButton, formatNumber } from '@column/column-ui-kit';
import {
  Button,
  DataTable,
  type DataTableColumnDef,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { useDialog } from '~/components/Dialog/Provider';
import { LogoLoading } from '~/elements';
import { useNavigate } from '~/lib/navigation';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';

import { ACHPositivePayRuleModal } from './_components/ACHPositivePayRuleModal';
import { BankAccountSectionProps } from './Section';

const formatCondition = (rule: AchPositivePayRuleResponse): { symbol: string | null; label: string } => {
  if (rule.amount_condition === 'any_amount') {
    return { symbol: null, label: 'Any amount' };
  }
  const formatted = formatNumber(rule.amount);
  return { symbol: rule.amount_condition === 'equal' ? '=' : '≤', label: formatted };
};

export const PageBankAccountsACHPositivePay: FC = () => {
  const { bankAccount } = useOutletContext<BankAccountSectionProps>();
  const { isAuthorized } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { openAlert } = useDialog();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AchPositivePayRuleResponse | null>(null);

  const canManage = isAuthorized({
    permission: 'bankAccounts',
    permissionLevel: 'update',
    bankAccount: { id: bankAccount.id, owners: bankAccount.owners },
  });

  const rulesQuery = useQuery({
    ...transferAchPositivePayRulesListAchPositivePayRulesByBankAccountOptions({
      path: { bank_account_id: bankAccount.id },
    }),
    enabled: !!bankAccount.id,
    retry: false,
  });

  // The tab is hidden for ineligible accounts, but the URL is reachable. Any settled error (400 non-business,
  // 404 positive-pay disabled, or transient) means the account can't manage rules, so redirect once it lands.
  useEffect(() => {
    if (rulesQuery.isError) {
      navigate(`${ROUTE.BANK_ACCOUNTS}/edit/${bankAccount.id}`, { replace: true });
    }
  }, [rulesQuery.isError, bankAccount.id, navigate]);

  const rules = useMemo(() => rulesQuery.data?.ach_positive_pay_rules ?? [], [rulesQuery.data]);
  const isLoading = rulesQuery.isLoading;

  const deleteRule = useMutation(transferAchPositivePayRulesDeleteAchPositivePayRuleMutation());

  const invalidateList = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: transferAchPositivePayRulesListAchPositivePayRulesByBankAccountQueryKey({
          path: { bank_account_id: bankAccount.id },
        }),
      }),
    [queryClient, bankAccount.id]
  );

  const openCreate = useCallback(() => {
    setEditingRule(null);
    setIsModalOpen(true);
  }, []);

  const openEdit = useCallback((rule: AchPositivePayRuleResponse) => {
    setEditingRule(rule);
    setIsModalOpen(true);
  }, []);

  const handleDelete = useCallback(
    (rule: AchPositivePayRuleResponse) => {
      const isLastRule = rules.length === 1;
      openAlert({
        headline: 'Delete ACH positive pay rule',
        content: isLastRule
          ? 'This is the last rule on this account. Deleting it turns ACH Positive Pay off — incoming ACH debits will no longer be filtered.'
          : 'This action cannot be undone. Debits matching this rule will no longer be allowed.',
        confirmButton: { label: 'Delete', variant: 'danger' },
        onSuccess: () => {
          deleteRule
            .mutateAsync({ path: { ach_positive_pay_rule_id: rule.ach_positive_pay_rule_id } })
            .then(() => {
              invalidateList();
              addSuccessNotification({ content: 'ACH positive pay rule deleted' });
            })
            .catch(() => {
              addDangerNotification({ content: 'Failed to delete the rule. Please try again.' });
            });
        },
      });
    },
    [rules.length, openAlert, deleteRule, invalidateList, addSuccessNotification, addDangerNotification]
  );

  const columns = useMemo<DataTableColumnDef<AchPositivePayRuleResponse>[]>(
    () => [
      {
        header: 'Description',
        accessor: 'description',
        width: 'minmax(240px, 1fr)',
        cell: ({ value }) => <span className="truncate font-medium text-foreground">{String(value)}</span>,
      },
      {
        header: 'ACH Company ID',
        accessor: 'ach_company_id',
        width: 'minmax(140px, max-content)',
        cell: ({ value }) => <span className="font-mono text-xs text-muted-foreground">{String(value)}</span>,
      },
      {
        header: 'Condition',
        accessor: 'amount_condition',
        width: 'minmax(140px, max-content)',
        cell: ({ row }) => {
          const { symbol, label } = formatCondition(row);
          return (
            <span className="font-medium text-foreground">
              {symbol ? (
                <>
                  <span className="text-gray-400">{symbol}</span> {label}
                </>
              ) : (
                label
              )}
            </span>
          );
        },
      },
      {
        header: '',
        accessor: 'ach_positive_pay_rule_id',
        width: 'minmax(40px, max-content)',
        className: 'justify-end',
        cell: ({ row }) =>
          canManage ? (
            <RowActionButton
              size="tiny"
              variant="secondary"
              icon={<Icon.Edit />}
              onClick={(event) => {
                event.stopPropagation();
                openEdit(row);
              }}
            >
              Edit
            </RowActionButton>
          ) : null,
      },
    ],
    [canManage, openEdit]
  );

  if (rulesQuery.isError) {
    return null;
  }

  return (
    <>
      {canManage && (
        <PageActions>
          <Button onClick={openCreate} size="sm">
            <Plus />
            Create Rule
          </Button>
        </PageActions>
      )}

      <Inner px="var(--page-margin)" pt="var(--page-margin)">
        <div className="flex flex-col gap-4">
          <DataTable<AchPositivePayRuleResponse>
            columns={columns}
            data={rules}
            isLoading={isLoading}
            loadingContent={<LogoLoading />}
            rowClassName="min-h-11"
            rowKey={(row) => row.ach_positive_pay_rule_id}
            onRowClick={canManage ? (row) => openEdit(row) : undefined}
            className={isLoading || rules.length === 0 ? 'min-h-[300px]!' : undefined}
            emptyContent={
              <Empty className="min-h-[260px] gap-3">
                <EmptyHeader>
                  <EmptyMedia variant="icon" className="mb-0">
                    <ShieldAlert />
                  </EmptyMedia>
                  <EmptyTitle>No rules set</EmptyTitle>
                  <EmptyDescription>
                    {canManage
                      ? "Create rules to restrict ACH Debits from this account. Debits that don't match a rule get returned."
                      : 'No rules have been configured for this account.'}
                  </EmptyDescription>
                </EmptyHeader>
                {canManage && (
                  <EmptyContent>
                    <Button onClick={openCreate} size="sm">
                      <Plus />
                      Create Rule
                    </Button>
                  </EmptyContent>
                )}
              </Empty>
            }
          />
        </div>
      </Inner>

      <ACHPositivePayRuleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        bankAccountId={bankAccount.id}
        rule={editingRule}
        onDelete={
          editingRule
            ? () => {
                setIsModalOpen(false);
                handleDelete(editingRule);
              }
            : undefined
        }
      />
    </>
  );
};
